import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getPeriods } from '../../../api/periods/get-periods'
import { YouthAndSportNumberInput } from '../../../lib/types/models/youth-and-sport-number/youth-and-sport-number-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<YouthAndSportNumberInput> = z.object({
  periodId: createNumberSchema('période'),
  countryId: createNumberSchema('pays'),
  number: createStringSchema("N° d'enregistrements"),
  centerIds: createNumberArraySchema('centre').optional()
})

const isFrance = (country?: { code: string; name: string }) =>
  !!country && (['FRA', 'FR'].includes(country.code) || country.name.toLowerCase() === 'france')

const YouthAndSportNumberForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<YouthAndSportNumberInput>) => {
  const form = useForm<YouthAndSportNumberInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const countryId = form.watch('countryId')
  const periodId = form.watch('periodId')

  const { data: periods } = useQuery({
    queryKey: ['periods', 'youth-and-sport-numbers'],
    queryFn: () => getPeriods<'LIGHT'>({ version: 'LIGHT', limit: 1000 })
  })

  const periodName = periods?.data.find((period) => period.id === periodId)?.name.toLowerCase()

  const { data: countries } = useQuery({
    queryKey: ['countries', 'youth-and-sport-numbers'],
    queryFn: () => getCountries({ limit: 1000 })
  })

  const selectedCountry = countries?.data.find((country) => country.id === countryId)
  const showCenters = isFrance(selectedCountry)

  const { data: centers } = useQuery({
    queryKey: ['centers', 'youth-and-sport-numbers', countryId, periodId],
    queryFn: () =>
      getCenters({
        version: 'BASIC',
        filter: {
          ...(countryId ? { countryIds: [countryId] } : {}),
          ...(periodName ? { periodNames: [periodName] } : {})
        }
      }),
    enabled: showCenters
  })

  useEffect(() => {
    if (!showCenters) form.setValue('centerIds', [])
  }, [form, showCenters])

  if (!periods || !countries) return <LoadingScreen />

  return (
    <Form form={form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
      >
        <Box className="flex flex-col gap-4">
          <InnerBox className="rounded-xl ">
            <Typography variant="body-medium" color={tailwindColors.primary.DEFAULT}>
              Number d'enregistrements
            </Typography>
            <div className="h-5" />
            <div className="grid grid-cols-3 gap-4 ">
              <FormField
                control={form.control}
                name="periodId"
                render={({ field }) => {
                  const period = periods.data.find((item) => item.id === field.value)
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledSelectInput
                          {...field}
                          required
                          label="Période"
                          options={periods.data.map((item) => ({
                            label: item.name,
                            value: item.id
                          }))}
                          selectedOption={getSelectedOption(field.value, period?.name)}
                          onChange={(option) => field.onChange(option)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />

              <FormField
                control={form.control}
                name="countryId"
                render={({ field }) => {
                  const country = countries.data.find((item) => item.id === field.value)
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledSelectInput
                          {...field}
                          required
                          searchable
                          label="Pays"
                          options={countries.data.map((item) => ({
                            label: item.name,
                            value: item.id
                          }))}
                          selectedOption={getSelectedOption(field.value, country?.name)}
                          onChange={(option) => field.onChange(option)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />

              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        label="N° d'enregistrements"
                        type="text"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {showCenters && (
              <>
                <div className="h-5" />
                <FormField
                  control={form.control}
                  name="centerIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledMultiSelectInput
                          {...field}
                          searchable
                          variant="orange"
                          label="Centre"
                          selectedOptions={field.value?.map((centerId) => ({
                            label:
                              centers?.data.find((center) => center.id === centerId)?.name ?? '',
                            value: centerId
                          }))}
                          options={
                            centers?.data.map((center) => ({
                              label: center.name,
                              value: center.id
                            })) ?? []
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}
          </InnerBox>

          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>
    </Form>
  )
}

export default YouthAndSportNumberForm
